import React, { useCallback, useContext, useState } from 'react';

import ActionModal from 'components/actionModal/ActionModal';
import Navbar, { NavbarItem } from 'components/actionModal/Navbar';
import CorporateAccountDetails from 'components/actionModal/stages/accountDetails/CorporateAccountDetails';
import Result from 'components/actionModal/stages/Result';
import { ActionModalMainContent } from 'components/actionModal/styledElements/stagesStyledElements';
import {
  CorporateAccount,
  editCorporateAccount,
} from 'modules/accounts/accountsServices/CorporateAccountsService';

import { CorporateAccountContext } from '../../context/CorporateAccountContext';
import useEditCorporateAccount from '../../hooks/useEditCorporateAccount';
import useSaveDataForEditCorporateAccount from '../../hooks/useSaveDataForEditCorporateAccount';
import { AccountCategory, AccountModalWindowStages } from '../../models';

const NAVBAR_ITEMS: NavbarItem<AccountModalWindowStages>[] = [
  {
    text: 'Business details',
    name: AccountModalWindowStages.AccountDetails,
  },
];

type EditCorporateModalProps = {
  accountForEditing: CorporateAccount;
  closeModalWindow: () => void;
};

const EditCorporateModal = ({
  accountForEditing,
  closeModalWindow,
}: EditCorporateModalProps) => {
  const [saved, setSaved] = useState(false);
  const [completionTitle, setCompletionTitle] = useState('');
  const [activatedStages, setActivatedStages] = useState<
    AccountModalWindowStages[]
  >([AccountModalWindowStages.AccountDetails]);
  const { groupName, saveGroupName, businessSector } = useContext(
    CorporateAccountContext
  );
  const [activeStage, setActiveStage] = useState<AccountModalWindowStages>(
    AccountModalWindowStages.AccountDetails
  );
  const { mutate } = useEditCorporateAccount(
    setCompletionTitle,
    editCorporateAccount
  );

  const toggleNextStage = useCallback((disable?: boolean) => {
    if (disable) {
      setActivatedStages([AccountModalWindowStages.AccountDetails]);
    } else {
      setActivatedStages(NAVBAR_ITEMS.map(({ name }) => name));
    }
  }, []);

  const goToResultStage = useCallback(() => {
    setActiveStage(AccountModalWindowStages.Result);
    mutate({
      data: {
        ...accountForEditing,
        companyName: groupName,
      },
    });
  }, [groupName, accountForEditing, mutate]);

  const changeActiveStage = useCallback((stage: number) => {
    const newActiveStage = NAVBAR_ITEMS[stage].name;

    setActiveStage(newActiveStage);
  }, []);

  useSaveDataForEditCorporateAccount(
    accountForEditing,
    setSaved,
    saveGroupName,
    saved
  );

  return (
    <ActionModal closeModalWindow={closeModalWindow}>
      {activeStage === AccountModalWindowStages.Result ? (
        <Result closeModal={closeModalWindow} title={completionTitle} />
      ) : (
        <>
          <Navbar
            showTitle
            title="Update account"
            activeStage={activeStage}
            accountCategory={AccountCategory.Group}
            activatedStages={activatedStages}
            changeActiveStage={changeActiveStage}
            navbarItems={NAVBAR_ITEMS}
          />
          <ActionModalMainContent>
            {activeStage === AccountModalWindowStages.AccountDetails && (
              <CorporateAccountDetails
                groupName={groupName}
                businessSector={businessSector}
                title="Edit business details"
                goToNextStage={goToResultStage}
                toggleNextStage={toggleNextStage}
              />
            )}
          </ActionModalMainContent>
        </>
      )}
    </ActionModal>
  );
};

export default EditCorporateModal;
